import { ImagePickerAsset } from "expo-image-picker";
import _ from "lodash";
import {
  Bookmark,
  Check,
  Search,
  SearchX,
  Users,
  X,
} from "lucide-react-native";
import {
  ComponentRef,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ScrollView } from "react-native";

import { useMediaQuery } from "@gluestack-ui/utils/hooks";

import { useUser } from "@clerk/expo";

import {
  DemographicsEditor,
  PanelCard,
  PanelCardSkeleton,
} from "@/components/panels";
import { GroupSizePicker } from "@/components/studies";
import {
  Box,
  Button,
  ButtonText,
  Center,
  ErrorWithRetry,
  HStack,
  Input,
  InputField,
  InputIcon,
  InputSlot,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import MobileSummaryCard from "@/components/ui/MobileSummaryCard";
import { useLocalization, useStudyContext } from "@/hooks";
import API from "@/hooks/api";
import {
  DemographicDistribution,
  DemographicOption,
  DemographicSetting,
  Group,
  Panel,
} from "@/types/resources";
import { GroupSize, ResourceType } from "@/types/utils";

/* ── Types ────────────────────────────────────────── */

type Demographics = DemographicSetting<
  Exclude<DemographicOption, "kids_age">
>[];

/* ── Constants ───────────────────────────────────── */

const DEBOUNCE_MS = 1000;
const DEFAULT_DEMOGRAPHICS: Demographics = [
  {
    demographic: "age",
    distribution: [{ label: "25-45", value: { max: 45, min: 25 }, weight: 1 }],
    label: "Age",
  },
];

/* ── Props ───────────────────────────────────────── */

interface GroupEditorProps {
  disabled?: boolean;
  group: Group;
  onChange?: (updated: Group) => void;
  onContinue?: () => void;
  resourceId: string;
  resourceType: ResourceType;
}

/* ── Component ───────────────────────────────────── */

const GroupEditor: FC<GroupEditorProps> = ({
  disabled: disabledProp = false,
  group,
  onChange,
  onContinue,
  resourceId,
  resourceType,
}) => {
  const { browserLocale, i18n } = useLocalization();
  const { studyType } = useStudyContext();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const [isDesktop] = useMediaQuery({ minWidth: 1024 });
  /* ── State ── */

  const [description, setDescription] = useState(group.description || "");
  const [demographics, setDemographics] = useState<Demographics>(
    group.demographics
  );
  const [media, setMedia] = useState<(ImagePickerAsset | string)[]>(
    group.media_urls
  );
  const [panelId, setPanelId] = useState<string | null>(
    group.panel?.id || null
  );
  const [panelSearch, setPanelSearch] = useState("");
  const [debouncedPanelSearch, setDebouncedPanelSearch] = useState("");
  const [size, setSize] = useState<number>(group.size);

  const panelsScrollRef = useRef<ComponentRef<typeof ScrollView>>(null);

  const debouncedPanelSearchFn = useMemo(
    () => _.debounce((text: string) => setDebouncedPanelSearch(text), 300),
    []
  );

  useEffect(() => {
    debouncedPanelSearchFn(panelSearch);
    return () => debouncedPanelSearchFn.cancel();
  }, [panelSearch, debouncedPanelSearchFn]);

  const mediaMapping = useMemo(
    () =>
      group.media_urls.reduce(
        (acc, url, index) => {
          acc[url] = group.media[index];
          return acc;
        },
        {} as Record<string, string>
      ),
    [group]
  );

  /* ── API hooks ── */

  const studyUpdateHook = API.Studies.useUpdateGroup(resourceId);
  const adhocUpdateHook = API.Adhoc.useUpdateGroup(resourceId);
  const { error, loading, updateGroup, uploadProgress } =
    resourceType === "study" ? studyUpdateHook : adhocUpdateHook;

  const newMediaCount = media.filter((m) => typeof m !== "string").length;

  const disabled = disabledProp || loading;

  const {
    count: panelsCount,
    error: panelsError,
    loading: panelsLoading,
    panels,
    refresh: refreshPanels,
  } = API.Panels.useGetPanels({ name: debouncedPanelSearch || undefined });

  /* ── Sync state from group prop ── */

  // Sync local state from the group prop during render (not in an effect) when
  // it changes; the initial values are seeded above.
  const [prevGroup, setPrevGroup] = useState(group);
  if (prevGroup !== group) {
    setPrevGroup(group);
    setDescription(group.description || "");
    setDemographics(group.demographics);
    setMedia(group.media_urls);
    setPanelId(group.panel?.id || null);
    setSize(group.size);
  }

  // Revert the editable fields during render (not in an effect) when a save
  // fails (error becomes set) or the group changes while an error is present.
  const [prevErrorRevert, setPrevErrorRevert] = useState({ error, group });
  if (prevErrorRevert.error !== error || prevErrorRevert.group !== group) {
    setPrevErrorRevert({ error, group });
    if (error) {
      setDemographics(group.demographics);
      setPanelId(group.panel?.id || null);
      setSize(group.size);
    }
  }

  /* ── Debounced auto-save ── */

  const debouncedSave = useMemo(
    () =>
      _.debounce(
        async (
          groupId: string,
          payload: {
            description: string | null;
            demographics: Demographics;
            media: (ImagePickerAsset | string)[];
            name: string | null;
            panel_id: string | null;
            size: GroupSize;
          }
        ) => {
          const updated = await updateGroup(groupId, payload);
          if (updated) onChange?.(updated);
        },
        DEBOUNCE_MS
      ),
    [onChange, updateGroup]
  );

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  type SaveOverrides = {
    description?: string | null;
    demographics?: Demographics;
    media?: (ImagePickerAsset | string)[];
    panel_id?: string | null;
    size?: GroupSize;
  };

  const buildPayload = useCallback(
    (overrides: SaveOverrides) => {
      const resolvedPanelId: string | null =
        "panel_id" in overrides ? (overrides.panel_id ?? null) : panelId;
      return {
        description: overrides.description ?? description ?? null,
        demographics: resolvedPanelId
          ? []
          : (overrides.demographics ?? demographics),
        media: (overrides.media ?? media).map((m) =>
          typeof m === "string" ? mediaMapping[m] || m : m
        ),
        name: group.name,
        panel_id: resolvedPanelId,
        size: (overrides.size ?? size) as GroupSize,
      };
    },
    [description, demographics, group.name, media, mediaMapping, panelId, size]
  );

  const triggerAutoSave = useCallback(
    (overrides: SaveOverrides) => {
      if (!group.id || !resourceId) return;
      debouncedSave(group.id, buildPayload(overrides));
    },
    [buildPayload, debouncedSave, group.id, resourceId]
  );

  const saveImmediately = useCallback(
    async (overrides: SaveOverrides) => {
      if (!group.id || !resourceId) return;
      debouncedSave.cancel();
      const updated = await updateGroup(group.id, buildPayload(overrides));
      if (updated) onChange?.(updated);
    },
    [buildPayload, debouncedSave, group.id, onChange, resourceId, updateGroup]
  );

  /* ── Handlers ── */

  const handleDemographicsChange = useCallback(
    (newDemographics: Demographics) => {
      setDemographics(newDemographics);
      if (panelId !== null) setPanelId(null);
      triggerAutoSave({ demographics: newDemographics, panel_id: null });
    },
    [panelId, triggerAutoSave]
  );

  const handlePanelSelect = useCallback(
    (panel: Panel) => {
      if (panel.id === panelId) {
        setPanelId(null);
        setDemographics(DEFAULT_DEMOGRAPHICS);
        saveImmediately({ demographics: DEFAULT_DEMOGRAPHICS, panel_id: null });
        return;
      }
      setPanelId(panel.id);
      setDemographics(panel.demographics);
      saveImmediately({ panel_id: panel.id });
      panelsScrollRef.current?.scrollTo({ animated: true, x: 0 });
    },
    [panelId, saveImmediately]
  );

  const handleSizeChange = useCallback(
    (newSize: GroupSize) => {
      setSize(newSize);
      triggerAutoSave({ size: newSize });
    },
    [triggerAutoSave]
  );

  /* ── Derived values ── */

  const selectedCountries = useMemo(() => {
    const cs = demographics.find((d) => d.demographic === "country");
    return cs ? cs.distribution.map((d) => d.value) : [];
  }, [demographics]);

  const ageDistribution = useMemo(() => {
    const a = demographics.find((d) => d.demographic === "age");
    return (a?.distribution ?? [
      { label: "25-45", value: { max: 45, min: 25 }, weight: 1 },
    ]) as DemographicDistribution<"age">[];
  }, [demographics]);

  const genderLabels = useMemo(() => {
    const g = demographics.find((d) => d.demographic === "gender");
    if (!g || g.distribution.length === 0)
      return i18n.t("audienceBuilder.allGenders");
    return g.distribution.map((d) => d.label).join(", ");
  }, [demographics, i18n]);

  const ageMin =
    ageDistribution.length > 0
      ? Math.min(...ageDistribution.map((d) => d.value.min))
      : 18;
  const ageMax =
    ageDistribution.length > 0
      ? Math.max(...ageDistribution.map((d) => d.value.max))
      : 65;

  const totalBehaviorFilters = demographics
    .filter((d) => d.demographic === "behavior")
    .reduce((sum, d) => sum + d.distribution.length, 0);

  const summaryItems: string[] = [
    i18n.t("audienceBuilder.genderAgeSummary", {
      ageMax,
      ageMin,
      gender: genderLabels,
    }),
    ...demographics
      .filter(
        (d) =>
          d.demographic !== "country" &&
          d.demographic !== "age" &&
          d.demographic !== "gender" &&
          d.demographic !== "behavior"
      )
      .map((d) =>
        i18n.t("audienceBuilder.selectedDemographics", {
          count: d.distribution.length,
          label: d.label,
        })
      ),
    ...(totalBehaviorFilters > 0
      ? [
          i18n.t("audienceBuilder.selectedDemographics", {
            count: totalBehaviorFilters,
            label: i18n.t("audienceBuilder.behaviors"),
          }),
        ]
      : []),
  ];

  /* ── Render ── */

  /* The editor (saved markets + demographics) scrolls. On desktop it sits
     beside the summary card; on mobile the summary becomes a pinned,
     collapsible card above it (see the return branches below). */
  const editorScroll = (
    <ScrollView
      className="w-full flex-1 lg:flex-[2]"
      contentContainerStyle={{
        gap: 40,
        paddingBottom: 32,
        paddingRight: isDesktop ? 12 : 0,
        paddingTop: isDesktop ? 0 : 12,
      }}
    >
      {/* Saved Markets */}
      {!(disabledProp && !panelId) &&
        (panelsLoading ||
          (!disabledProp && panelsError) ||
          panelsCount > 0 ||
          !!debouncedPanelSearch) && (
          <VStack className="gap-3">
            <HStack className="flex-col items-stretch gap-2 lg:flex-row lg:items-end lg:justify-between">
              <HStack className="items-center gap-2">
                <Bookmark color="rgb(var(--color-typography-400))" size={16} />
                {panelsLoading && !debouncedPanelSearch ? (
                  <Skeleton className="h-4 w-28 rounded" />
                ) : (
                  <Text className="text-sm font-semibold text-typography-900">
                    {disabledProp
                      ? i18n.t("audienceBuilder.savedMarket")
                      : i18n.t("audienceBuilder.savedMarkets")}
                  </Text>
                )}
              </HStack>
              {!disabledProp && (
                <Input
                  className="w-full lg:w-52 border border-outline-200 rounded-full bg-background-50"
                  size="sm"
                >
                  <InputSlot className="pl-2.5">
                    <InputIcon
                      as={Search}
                      className="text-typography-400"
                      size="xs"
                    />
                  </InputSlot>
                  <InputField
                    onChangeText={setPanelSearch}
                    placeholder={i18n.t("misc.search")}
                    value={panelSearch}
                  />
                  {panelSearch.length > 0 && (
                    <InputSlot
                      className="pr-2.5"
                      onPress={() => setPanelSearch("")}
                    >
                      <InputIcon
                        as={X}
                        className="text-typography-400"
                        size="xs"
                      />
                    </InputSlot>
                  )}
                </Input>
              )}
            </HStack>

            {disabledProp && group.panel ? (
              /* Same top inset as the list so the applied badge isn't clipped. */
              <Box className="pt-3">
                <PanelCard applied disabled panel={group.panel} />
              </Box>
            ) : panelsError ? (
              <ErrorWithRetry
                error={panelsError}
                onRetry={refreshPanels}
                title={i18n.t("panels.errorLoadingPanels")}
              />
            ) : panelsLoading ? (
              <ScrollView
                contentContainerStyle={{
                  gap: 12,
                  paddingTop: 12,
                  paddingBottom: 16,
                  paddingRight: 8,
                }}
                horizontal
              >
                {[0, 1, 2].map((k) => (
                  <PanelCardSkeleton key={k} />
                ))}
              </ScrollView>
            ) : panels.length > 0 ? (
              <ScrollView
                contentContainerStyle={{
                  gap: 12,
                  paddingTop: 12,
                  paddingBottom: 16,
                  paddingRight: 8,
                }}
                horizontal
                ref={panelsScrollRef}
              >
                {panels.map((panel) => (
                  <PanelCard
                    applied={panelId === panel.id}
                    disabled={disabled}
                    key={panel.id}
                    onPress={
                      disabled ? undefined : () => handlePanelSelect(panel)
                    }
                    panel={panel}
                  />
                ))}
              </ScrollView>
            ) : debouncedPanelSearch ? (
              <Center className="py-6">
                <VStack className="items-center gap-3">
                  <SearchX color="rgb(var(--color-typography-400))" size={32} />
                  <Text className="text-sm font-medium text-typography-700">
                    {i18n.t("misc.noSearchResults")}
                  </Text>
                  <Text className="text-xs text-typography-500">
                    {i18n.t("misc.noSearchResultsDescription")}
                  </Text>
                </VStack>
              </Center>
            ) : null}
          </VStack>
        )}

      <DemographicsEditor
        demographics={demographics}
        disabled={disabled || !isAdmin}
        onChange={handleDemographicsChange}
        readonly={disabledProp || !isAdmin}
      />
    </ScrollView>
  );

  /* Summary body (sample size, checklist, CTA). Rendered inside the dark
     side card on desktop, and inside the pinned collapsible card on mobile. */
  const summaryBody = (
    <>
      {/* Sample size */}
      <Box className="mb-5">
        <GroupSizePicker
          disabled={disabled}
          onChange={handleSizeChange}
          size={size}
          studyType={studyType}
        />
      </Box>

      {/* Summary checklist */}
      <VStack className="gap-2 mb-6">
        {[
          i18n.t("audienceBuilder.countryCount", {
            count: selectedCountries.length,
          }),
          ...summaryItems,
        ].map((item) => (
          <HStack className="items-center gap-2" key={item}>
            <Check color="rgb(var(--color-primary-400))" size={14} />
            <Text className="text-sm text-white/80">{item}</Text>
          </HStack>
        ))}
      </VStack>

      {onContinue && (
        <Button
          className="w-full rounded-full py-3.5 mt-4 bg-primary-500 data-[hover=true]:bg-primary-400 data-[active=true]:bg-primary-600"
          isDisabled={loading || (!isAdmin && !panelId)}
          onPress={onContinue}
        >
          <ButtonText className="font-medium text-white">
            {uploadProgress > 0
              ? i18n.t("misc.uploadingMedia", {
                  count: Math.max(1, newMediaCount),
                  percent: i18n.numberToPercentage(uploadProgress * 100, {
                    precision: 0,
                  }),
                })
              : loading
                ? i18n.t("misc.saving")
                : i18n.t("audienceBuilder.continue")}
          </ButtonText>
        </Button>
      )}
      <Box className="h-5 mt-1 items-center justify-center">
        {error ? (
          <Text className="text-xs text-red-400 text-center">{error}</Text>
        ) : !isAdmin && !panelId ? (
          <Text className="text-xs text-white/40 text-center">
            {i18n.t("audienceBuilder.selectSegmentHint")}
          </Text>
        ) : null}
      </Box>
    </>
  );

  const summaryTitle = i18n.t("audienceBuilder.audienceSummary");
  const summaryPreview = (
    <HStack className="items-center gap-1.5">
      <Users color="rgb(var(--color-primary-400))" size={14} />
      <Text className="text-white font-semibold text-base">
        {`${i18n.numberToDelimited(size, {
          delimiter: browserLocale.digitGroupingSeparator!,
        })} · ${i18n.t("audienceBuilder.countryCount", {
          count: selectedCountries.length,
        })}`}
      </Text>
    </HStack>
  );

  /* Mobile: the summary becomes a collapsible card pinned to the top, so the
     CTA and sample size stay reachable while the editor scrolls beneath. */
  if (!isDesktop) {
    return (
      <VStack className="flex-1 min-h-0 gap-3">
        <MobileSummaryCard preview={summaryPreview} title={summaryTitle}>
          {summaryBody}
        </MobileSummaryCard>
        {editorScroll}
      </VStack>
    );
  }

  /* Desktop: editor scrolls beside a fixed summary card. */
  return (
    <HStack className="flex-1 min-h-0 gap-2">
      {editorScroll}
      <VStack className="lg:flex-1">
        <Box className="bg-typography-900 rounded-2xl p-8 pb-4">
          <Text className="text-[13px] uppercase tracking-widest text-white/50 font-medium mb-4">
            {summaryTitle}
          </Text>
          {summaryBody}
        </Box>
      </VStack>
    </HStack>
  );
};

export default GroupEditor;
